import { useState } from "react";
import axios from "axios";
import { Flag, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "../App";

const REASONS = [
  { id: "spam", label: "Spam or scam" },
  { id: "harassment", label: "Harassment or bullying" },
  { id: "hate", label: "Hate speech" },
  { id: "explicit", label: "Explicit / NSFW" },
  { id: "scam", label: "Fraud / impersonation" },
  { id: "other", label: "Something else" },
];

// Compact "Report" button + inline reason picker.
// Props: contentType ('post' | 'user' | 'artist' | 'comment'), contentId, token, className
const ReportButton = ({ contentType, contentId, token, className = "", iconOnly = false }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("spam");
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!token) { toast.error("Sign in to report content"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/moderation/report`, {
        content_type: contentType, content_id: contentId, reason, details
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message || "Report submitted");
      setOpen(false);
      setDetails("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit report");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={`text-[#8A92A0] hover:text-[#D14A85] transition-colors text-xs flex items-center gap-1 ${className}`}
        title="Report"
        data-testid={`report-btn-${contentId}`}
      >
        <Flag className="w-3.5 h-3.5" />
        {!iconOnly && <span>Report</span>}
      </button>

      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4" onClick={() => setOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="card max-w-md w-full p-6" data-testid="report-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-['Unbounded'] text-lg font-bold text-white mb-1">Report this {contentType}</h3>
                <p className="text-xs text-[#8A92A0]">Our team reviews every report. Thank you for keeping the community safe.</p>
              </div>
              <button onClick={() => setOpen(false)} className="text-[#8A92A0] hover:text-white" data-testid="close-report-modal"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-2 mb-4">
              {REASONS.map(r => (
                <label key={r.id} className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer transition-colors ${reason === r.id ? "border-[#EC008C] bg-[#EC008C]/10" : "border-[#1A2030] hover:border-[#2A3040]"}`}>
                  <input
                    type="radio"
                    checked={reason === r.id}
                    onChange={() => setReason(r.id)}
                    className="accent-[#EC008C]"
                    data-testid={`reason-${r.id}`}
                  />
                  <span className="text-sm text-white">{r.label}</span>
                </label>
              ))}
            </div>

            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Optional: what should the team know?"
              rows={3}
              className="w-full bg-[#0A0E18] border border-[#1A2030] rounded-lg px-3 py-2 text-sm text-white placeholder-[#6A7280] focus:outline-none focus:border-[#EC008C]/50 resize-none mb-4"
              data-testid="report-details"
            />

            <div className="flex gap-2 justify-end">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm text-[#8A92A0] hover:text-white" data-testid="cancel-report-btn">Cancel</button>
              <button
                onClick={submit}
                disabled={submitting}
                className="px-4 py-2 rounded-lg bg-[#D14A85] text-white hover:bg-[#A04545] text-sm font-semibold disabled:opacity-60 flex items-center gap-2"
                data-testid="submit-report-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flag className="w-4 h-4" />}
                Submit report
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReportButton;
